<template>
  <div id="tenders-comparison">
    <div id="tenders-comparison-data">
      <table class="excel">
        <thead>
          <tr>
            <th
              id="table-title"
              class="title note column excel-title excel-block excel-intro excel-top-center"
              rowspan="2"
              style="text-align: center; min-width: 320px;"
            >
              {{ $t('comparison.title') }}
            </th>
            <th
              v-for="(bid, index) in bids"
              :key="buildRandomKey(index)"
              colspan="4"
              class="title column excel-title excel-block excel-top-center"
              style="text-align: center;"
              :class="clsForCell(index)"
            >
              {{ deep(bid, 'company.name') || '' }}
            </th>
          </tr>
          <tr>
            <template v-for="(bid, index) in bids">
              <th
                :key="buildRandomKey(index)"
                :class="clsForCell(index)"
                class="title excel-subtitle excel-block excel-top-center"
                style="text-align: center;"
              >
                {{ $t('tender.section.qty') }}
              </th>
              <th
                :key="buildRandomKey(index)"
                :class="clsForCell(index)"
                class="title excel-subtitle excel-block excel-top-center"
                style="text-align: center;"
              >
                {{ $t('tender.section.unit') }}
              </th>
              <th
                :key="buildRandomKey(index)"
                :class="clsForCell(index)"
                class="title right excel-subtitle excel-block excel-top-center section-unit-rate"
              >
                {{ $t('tender.section.unit') }} {{ $t('tender.section.rate') }}
              </th>
              <th
                :key="buildRandomKey(index)"
                :class="clsForCell(index)"
                class="title right excel-subtitle excel-block excel-top-center section-amount excel-amount"
              >
                {{ $t('comparison.amount') }}/{{ currencyCode }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, iIndex) in services">
            <tr :key="buildRandomKey(iIndex)">
              <td class="border-left-right bold excel-block excel-top-center">
                {{ String.fromCharCode(65 + iIndex) }}&nbsp;&nbsp;&nbsp;&nbsp;{{ item.title }}
              </td>
              <td
                v-for="(bid, vIndex) in bids"
                :key="vIndex"
                :class="clsForCell(vIndex)"
                class="border-right excel-block"
                colspan="4"
              />
            </tr>
            <tr
              v-for="(lineItem, lIndex) in item.lineItems"
              :key="lIndex"
            >
              <td
                class="border-left-right excel-block"
                style="padding-left: 3rem;  text-indent:-1.5rem;"
              >
                {{ lineItem.itemNo }}. {{ lineItem.itemDesc }}
              </td>
              <template v-for="(bid, index) in bids">
                <template v-if="isBidValid(bid)">
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ bid.content.service.bidItemQtys[lineItem.id] || lineItem.itemQty }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ lineItem.itemUnit }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="right excel-number section-unit-rate"
                    :class="clsForCell(index)"
                    :currency-for-excel="formatCurrencyForExcel(deep(bid, `content.service.bidItemRates.${lineItem.id}`), notApplicable)"
                  >
                    {{ formatAmount(deep(bid, `content.service.bidItemRates.${lineItem.id}`), notApplicable) }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="border-right right excel-number excel-block section-amount excel-amount"
                    :class="clsForCell(index, lineItem.total[bid.id] === lineItem.total.min, lineItem.total[bid.id] === lineItem.total.max)"
                    :currency-for-excel="formatCurrencyForExcel(lineItem.total[bid.id], notApplicable)"
                  >
                    {{ formatAmount(lineItem.total[bid.id], notApplicable) }}
                  </td>
                </template>
                <template v-else>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ renderedEmptyValue(bid, iIndex, lIndex) }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ renderedEmptyValue(bid, iIndex, lIndex) }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                    class="right excel-number section-unit-rate"
                  >
                    {{ renderedEmptyValue(bid, iIndex, lIndex) }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="border-right right excel-number excel-block section-amount excel-amount"
                    :class="clsForCell(index)"
                  >
                    {{ renderedEmptyValue(bid, iIndex, lIndex) }}
                  </td>
                </template>
              </template>
            </tr>
            <tr :key="buildRandomKey(iIndex)">
              <td class="border-top-right bold right excel-block excel-number">
                {{ $t('comparison.subTotal') }} {{ String.fromCharCode(65 + iIndex) }}{{ item.forReference ? `(${$t('tender.section.notIncluded')})`: '' }}
              </td>
              <template v-for="(bid, vIndex) in bids">
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="2"
                />
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="bold right excel-number"
                >
                  {{ `${formatPercentage(item.subTotalPercentage[bid.id])}` }}
                </td>
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="border-top-right bold right excel-number excel-block"
                  :currency-for-excel="formatCurrencyForExcel(item.subTotal[bid.id], notApplicable)"
                >
                  {{ (isBidValid(bid) && item.subTotal[bid.id] !== notApplicable) ?
                    `${formatAmount(item.subTotal[bid.id])} ` :
                    notApplicable }}
                </td>
              </template>
            </tr>
          </template>

          <template v-if="showOthers">
            <tr>
              <td class="border-left-right bold excel-block excel-top-center">
                {{ $t('others') }}
              </td>
              <td
                v-for="(bid, vIndex) in bids"
                :key="vIndex"
                :class="clsForCell(vIndex)"
                class="border-right excel-block"
                colspan="4"
              />
            </tr>
            <tr>
              <td
                class="border-left-right excel-block"
                style="padding-left: 3rem;  text-indent:-1.5rem;"
              >
                {{ $t('description') }}
              </td>
              <template v-for="(bid, index) in bids">
                <td
                  v-if="isBidValid(bid)"
                  :key="buildRandomKey(index)"
                  colspan="4"
                  :class="clsForCell(index)"
                  class="border-right"
                >
                  {{ deep(bid,'content.service.otherItem.desc') }}
                </td>
                <td
                  v-else
                  :key="buildRandomKey(index)"
                  colspan="4"
                  :class="clsForCell(index)"
                  class="border-right"
                >
                  {{ emptyValue }}
                </td>
              </template>
            </tr>
            <tr>
              <td
                class="border-left-right excel-block"
                style="padding-left: 3rem;  text-indent:-1.5rem;"
              >
                {{ $t('comparison.amount') }}
              </td>
              <template v-for="(bid, index) in bids">
                <template v-if="isBidValid(bid)">
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    1
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    Lot
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="right excel-number"
                    :class="clsForCell(index)"
                    :currency-for-excel="formatCurrencyForExcel(deep(bid, 'content.service.otherItem.rate'), notApplicable)"
                  >
                    {{ formatAmount(deep(bid, 'content.service.otherItem.rate'), notApplicable) }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="border-right right excel-number excel-block"
                    :class="clsForCell(index)"
                    :currency-for-excel="formatCurrencyForExcel(deep(bid, 'content.service.otherItem.rate'))"
                  >
                    {{ formatAmount(deep(bid, 'content.service.otherItem.rate')) }}
                  </td>
                </template>
                <template v-else>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ emptyValue }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                  >
                    {{ emptyValue }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    :class="clsForCell(index)"
                    class="right excel-number"
                  >
                    {{ emptyValue }}
                  </td>
                  <td
                    :key="buildRandomKey(index)"
                    class="border-right right excel-number excel-block"
                    :class="clsForCell(index)"
                  >
                    {{ emptyValue }}
                  </td>
                </template>
              </template>
            </tr>
            <tr>
              <td class="border-top-right bold right excel-block excel-number">
                {{ $t('comparison.subTotal') }} {{ $t('others') }}
              </td>
              <template
                v-for="(bid, vIndex) in bids"
              >
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="2"
                />
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="bold right excel-number"
                >
                  {{ `${formatPercentage(deep(bid, 'content.service.otherItemPercentage'))}` }}
                </td>
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="border-top-right bold right excel-number excel-block"
                  :currency-for-excel="formatCurrencyForExcel(deep(bid, 'content.service.otherItem.rate'), notApplicable)"
                >
                  {{ isBidValid(bid) ?
                    `${formatAmount(deep(bid, 'content.service.otherItem.rate'))}` :
                    notApplicable }}
                </td>
              </template>
            </tr>
          </template>

          <template v-if="showOtherItems">
            <tr>
              <td class="border-left-right bold excel-block excel-top-center">
                {{ $t('others') }}
              </td>
              <td
                v-for="(bid, vIndex) in bids"
                :key="buildRandomKey(vIndex)"
                :class="clsForCell(vIndex)"
                class="border-right excel-block"
                colspan="4"
              />
            </tr>
            <template v-for="(item, cIndex) in bidItemContent">
              <tr :key="buildRandomKey(cIndex)">
                <td
                  class="border-left-right excel-block"
                  style="padding-left: 3rem;  text-indent:-1.5rem;"
                >
                  {{ cIndex+1 }}. {{ $t('description') }}
                </td>
                <template v-for="(content, tIndex) in item">
                  <td
                    v-if="isValidateOtherContent(content)"
                    :key="buildRandomKey(tIndex)"
                    :class="clsForCell(tIndex)"
                    class="border-right excel-block"
                    colspan="4"
                  >
                    {{ content ? deep(content, 'itemDesc') : notApplicable }}
                  </td>
                  <td
                    v-else
                    :key="buildRandomKey(tIndex)"
                    :class="clsForCell(tIndex)"
                    class="border-right excel-block"
                    colspan="4"
                  >
                    {{ emptyValue }}
                  </td>
                </template>
              </tr>
              <tr :key="buildRandomKey(cIndex)">
                <td
                  class="border-left-right excel-block"
                  style="padding-left: 3rem;  text-indent:-1.5rem;"
                >
                  {{ $t('comparison.amount') }}
                </td>
                <template v-for="(content, tIndex) in item">
                  <template v-if="isValidateOtherContent(content)">
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                    >
                      {{ content ? deep(content, 'itemQty') : notApplicable }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                    >
                      {{ content ? deep(content, 'itemUnit') : notApplicable }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                      class="right excel-number"
                      :currency-for-excel="formatCurrencyForExcel(deep(content, 'rate'), notApplicable)"
                    >
                      {{ formatAmount(deep(content, 'rate'), notApplicable) }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      class="border-right right excel-number excel-block"
                      :class="clsForCell(tIndex)"
                      :currency-for-excel="formatCurrencyForExcel(deep(content, 'total'), notApplicable)"
                    >
                      {{ formatAmount(deep(content, 'total'), notApplicable) }}
                    </td>
                  </template>
                  <template v-else>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                    >
                      {{ emptyValue }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                    >
                      {{ emptyValue }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                      class="right excel-number"
                    >
                      {{ emptyValue }}
                    </td>
                    <td
                      :key="buildRandomKey(tIndex)"
                      :class="clsForCell(tIndex)"
                      class="border-right right excel-number excel-block"
                    >
                      {{ emptyValue }}
                    </td>
                  </template>
                </template>
              </tr>
            </template>
            <tr>
              <td class="border-top-right bold right excel-block excel-number">
                {{ $t('comparison.subTotal') }} {{ $t('others') }}
              </td>
              <template v-for="(bid, vIndex) in bids">
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="2"
                />
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="bold right excel-number"
                >
                  {{ `${formatPercentage(deep(bid, 'content.service.multiOtherItemsPercentage'))}` }}
                </td>
                <td
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  class="border-top-right bold right excel-number excel-block"
                  :currency-for-excel="formatCurrencyForExcel(formatOtherTotalFee(bid), notApplicable)"
                >
                  {{ showAmount(bid) }}
                </td>
              </template>
            </tr>
          </template>

          <template v-if="showDiscount">
            <tr>
              <td class="bold right border-right excel-block excel-number">
                {{ $t('comparison.totalBeforeDiscount') }}({{ $t('comparison.hk') }}$)
              </td>
              <template v-for="(bid, vIndex) in bids">
                <td
                  v-if="isBidValid(bid)"
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="4"
                  class="bold border-right right excel-number excel-block"
                >
                  {{ formatAmount(bid.content.service.totalBeforeDiscount) }}
                </td>
                <td
                  v-else
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="4"
                  class="bold border-right right excel-number excel-block"
                >
                  {{ notApplicable }}
                </td>
              </template>
            </tr>
            <tr>
              <td class="bold right border-right excel-block excel-number">
                {{ $t('comparison.discount') }}
              </td>
              <template v-for="(bid, vIndex) in bids">
                <td
                  v-if="isBidValid(bid)"
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="4"
                  class="bold border-right right excel-number excel-block"
                >
                  {{ formatDiscount(bid.content.service.discountValue, bid.content.service.discountType) }}
                </td>
                <td
                  v-else
                  :key="buildRandomKey(vIndex)"
                  :class="clsForCell(vIndex)"
                  colspan="4"
                  class="bold border-right right excel-number excel-block"
                >
                  {{ notApplicable }}
                </td>
              </template>
            </tr>
          </template>

          <tr>
            <td class="bold right border-right excel-block excel-number">
              {{ $t('tender.section.total') }}
            </td>
            <template v-for="(bid, vIndex) in bids">
              <td
                v-if="isBidValid(bid)"
                :key="buildRandomKey(vIndex)"
                colspan="4"
                class="bold border-right right excel-number excel-block"
                :class="clsForCell(vIndex, bid.content.service.totalFee === min, bid.content.service.totalFee === max)"
                :currency-for-excel="formatCurrencyForExcel(bid.content.service.totalFee)"
              >
                {{ formatAmount(bid.content.service.totalFee) }}
              </td>
              <td
                v-else
                :key="buildRandomKey(vIndex)"
                colspan="4"
                class="bold border-right right excel-number excel-block"
                :class="clsForCell(vIndex)"
              >
                {{ notApplicable }}
              </td>
            </template>
          </tr>
          <tr v-if="shouldShowRemarksRow" class="vendor-remarks">
            <td class="bold right border-right excel-block excel-number">
              {{ $t('comparison.vendorRemarks') }}
            </td>
            <template v-for="(remark, vIndex) in vendorRemarks">
              <td
                :key="buildRandomKey(vIndex)"
                class="border-right right excel-block wrap-cell"
                colspan="4"
              >
                <pre v-html="remark" />
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="actions">
      <button
        type="button"
        class="btn btn--orange"
        @click="onExport"
      >
        {{ $t('comparison.exportToExcel') }}
      </button>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import CurrencyFormatter from 'currency-formatter'
import { mapActions, mapGetters } from 'vuex'
import { computed, ref, unref } from 'vue-demi'
import HTTP from '@/utils/http'
import * as styles from '@/utils/styles'
import * as time from '@/utils/time'
import * as xfs from '@/utils/xfs'
import { bidStatusMap, discountTypes, vendorBidStatus } from '@/utils/locale'
import { formatExcelCurrencies, formatCurrencyForExcel } from '@/utils/html2xlsxHdUtil'
import { log } from '@/utils'
import i18n from '@/utils/i18n'

export default {
  name: 'ComparisonTable',
  setup () {
    const notApplicable = i18n.t('remarkNA')
    const emptyValue = i18n.t('emptyValue')
    const bids = ref([])
    const getRemarks = (bid) => {
      return lodash.get(bid, 'content.service.remark', '') || ''
    }
    const vendorRemarks = computed(() => {
      return bids.value.map(bid => getRemarks(bid))
    })
    const shouldShowRemarksRow = computed(() => {
      return unref(vendorRemarks).some(item => Boolean(item.trim()))
    })
    return {
      bids,
      emptyValue,
      notApplicable,
      vendorRemarks,
      shouldShowRemarksRow,
      buildRandomKey: (idx) => `${Math.random().toString(36).substring(2, 15)}_${idx}`,
      getRemarks,
    }
  },
  data () {
    return {
      services: [],
      showOthers: true,
      showDiscount: false,
      currencyFormatter: CurrencyFormatter,
      discountTypes: discountTypes,
      maxOtherItemsLength: 0,
      bidItemContent: [],
      max: Number.MIN_SAFE_INTEGER,
      min: Number.MAX_SAFE_INTEGER,
      deep,
    }
  },
  computed: {
    ...mapGetters('tenders', {
      currentTender: 'currentTender',
    }),
    currencyCode () {
      return lodash.get(this.currentTender, 'purchaseRequisition.currencyCode')
    },
    showOtherItems () {
      return deep(this.currentTender, 'general.allowServiceAddOthers') && this.maxOtherItemsLength > 0
    },
  },
  async mounted () {
    await this.initTable()
  },
  methods: {
    ...mapActions('tenders', [
      'loadTender',
      'validateRoute',
    ]),
    formatCurrencyForExcel,
    clsForCell (index, lowest = false, highest = false) {
      const cls = [(index % 2) ? 'excel-odd' : 'excel-even']
      if (lowest === true) {
        cls.push('lowestLine')
      } else if (highest === true) {
        cls.push('highestLine')
      }
      return cls
    },
    formatOtherTotalFee (bid) {
      const result = deep(bid, 'content.service.totalOfOthers')
      const otherItems = deep(bid, 'content.service.otherItems') || []
      // If there are items, even the amount is 0, show amount, otherwise show N/A
      return otherItems.length ? result : undefined
    },
    showAmount (bid) {
      const result = this.formatOtherTotalFee(bid)
      return `${this.formatAmount(result, this.notApplicable)}`
    },
    async onExport () {
      const excelName = `PR${this.currentTender.general.prNumber}-Comparison.xlsx`
      const target = document.getElementById('tenders-comparison-data')
      const table = `${styles.comparison}${formatExcelCurrencies(target.innerHTML, this.currencyCode)}`

      this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
      try {
        const response = await HTTP.post('/fileman/', { table }, { responseType: 'arraybuffer' })
        await time.waitFor(3000)
        xfs.save(response, excelName)
      } catch (err) {
        log(err.message)
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },

    filterServices (services) {
      const clonedServices = lodash.cloneDeep(services)
      return Object.values(clonedServices).map((item) => {
        item.subTotal = {}
        item.subTotalPercentage = {}
        const filteredLineItems = item.lineItems
          .filter((line) => (line.addendumLabel !== 'D'))
        delete item.lineItems
        item.lineItems = filteredLineItems
        return item
      }).filter((item) => (item.addendumLabel !== 'D' && (!item.forReference || item.lineItems.length > 0)))
    },

    getSortedBids (bids) {
      let validBids = []
      const otherBids = []
      if (this.currentTender) {
        (this.currentTender.bids || []).forEach((rec) => {
          const newRec = lodash.cloneDeep(rec)
          if (newRec.status !== vendorBidStatus.SUBMITTED) {
            newRec.content = null
          }
          (newRec.content && newRec.content.service)
            ? validBids.push(newRec)
            : otherBids.push(newRec)
        })
        validBids = lodash.sortBy(validBids, bid => this.getFee(bid))
      }
      return validBids.concat(otherBids)
    },

    getMultiOtherItems (bids) {
      let bidContents = []
      bids.forEach((bid, index) => {
        const content = bid.content ? (lodash.get(bid, 'content.service.otherItems', [])) : ''
        bidContents.push(content)
        if (this.maxOtherItemsLength < content.length) {
          this.maxOtherItemsLength = content.length
        }
      })
      let othersArray = new Array(this.maxOtherItemsLength)
      for (let i = 0; i < othersArray.length; i++) {
        bidContents.forEach((rec) => {
          const originContent = lodash.cloneDeep(othersArray[i]) || []
          const result = rec ? originContent.concat(rec[i]) : originContent.concat(rec)
          othersArray.splice(i, 1, result)
        })
      }
      this.bidItemContent = othersArray
    },

    isValidateOtherContent (content) {
      // When no content, it is empty string, when empty string, show '--' or show N/A
      return typeof content !== 'string'
    },

    getFee (bid) {
      return lodash.get(bid, 'content.service.totalFee') || 0
    },

    async initTable () {
      await this.loadTender(this.$router.currentRoute.params.id)
      if (!this.validateRoute()) return
      if (this.currentTender) {
        this.bids = this.getSortedBids()
        this.services = this.filterServices(this.currentTender.service || [])
        this.showOthers = this.bids.some(bid => {
          const otherItemRate = deep(bid, 'content.service.otherItem.rate')
          return this.formatAmount(otherItemRate, this.notApplicable) !== this.notApplicable
        })
        this.showDiscount = this.bids.some(bid => {
          const discount = deep(bid, 'content.service.discountValue')
          return discount > 0
        })
      }
      this.getMultiOtherItems(this.bids)
      this.setSubTotals()
    },

    setSubTotals () {
      Object.values(this.bids).forEach((bid) => {
        if (bid.content && bid.content.service) {
          const bidService = bid.content.service
          const totalBeforeDiscount = this.getTotalBeforeDiscount(bidService)
          bidService.totalBeforeDiscount = totalBeforeDiscount
          if (bidService.totalFee < this.min) {
            this.min = bidService.totalFee
          }
          if (bidService.totalFee > this.max) {
            this.max = bidService.totalFee
          }

          bidService.otherItemPercentage = typeof bidService.otherItem.rate === 'undefined'
            ? this.notApplicable : ((bidService.otherItem.rate * 100) / totalBeforeDiscount).toFixed(2)

          bidService.multiOtherItemsPercentage = (!bidService.otherItems || !bidService.otherItems.length)
            ? this.notApplicable : ((bidService.totalOfOthers * 100) / totalBeforeDiscount).toFixed(2)

          Object.values(this.services).forEach((service) => {
            service.subTotal[bid.id] = service.lineItems.map((rec) => {
              const bidQty = bidService.bidItemQtys[rec.id]
              const qtyEmpty = typeof bidQty === 'string' && bidQty.length === 0
              if (qtyEmpty) {
                bidService.bidItemQtys[rec.id] = this.notApplicable
              }
              const rate = bidService.bidItemRates && bidService.bidItemRates[rec.id]
              const rateNotChanged = (typeof rate === 'undefined')
              const rateEmpty = typeof rate === 'string' && rate.length === 0

              if (!rec.total) {
                rec.total = {}
              }
              if (qtyEmpty || rateNotChanged || rateEmpty) {
                rec.total[bid.id] = this.notApplicable
              } else {
                rec.total[bid.id] = Number((qtyEmpty || typeof bidQty === 'undefined') ? rec.itemQty : bidQty) * Number((bidService.bidItemRates && bidService.bidItemRates[rec.id]) || 0)
                rec.total.min = Math.min(...Object.values(rec.total).filter(total => !isNaN(total)))
                rec.total.max = Math.max(...Object.values(rec.total).filter(total => !isNaN(total)))
              }
              return rec.total[bid.id]
            }).reduce((x, y) => {
              if (isNaN(x)) {
                return y
              } else if (isNaN(y)) {
                return x
              } else {
                return (x || 0) + (y || 0)
              }
            })
            service.subTotalPercentage[bid.id] = service.forReference ? this.notApplicable
              : ((service.subTotal[bid.id] * 100) / totalBeforeDiscount).toFixed(2)
          })
        } else {
          Object.values(this.services).forEach((service) => {
            service.subTotal[bid.id] = this.notApplicable
            service.subTotalPercentage[bid.id] = this.notApplicable
          })
        }
      })
      /* eslint-enable no-param-reassign */
    },
    getResponseData (response) {
      if (!response) {
        return ''
      }
      const data = response.data.data
      return data
    },
    formatDiscount (discountValue, discountType) {
      if (discountType === this.discountTypes.DOLLAROFF) {
        return `${this.currencyCode} ${discountValue} ${discountType}`
      }
      return `${discountValue}${discountType}`
    },
    formatAmount (amount, defaultValue) {
      const invalidStr = typeof amount === 'string' && (amount.length === 0 || isNaN(amount))
      if ((invalidStr || typeof amount === 'undefined') && defaultValue) {
        return defaultValue
      }
      return `${this.currencyCode}${this.currencyFormatter.format(amount, 'USD')}`
    },
    formatPercentage (percentage) {
      return isNaN(percentage) ? this.notApplicable : `${percentage}%`
    },
    getTotalBeforeDiscount (service) {
      if (service.discountType) {
        return (service.discountType === this.discountTypes.discountTypes)
          ? (service.totalFee * 100) / (100 - service.discountValue)
          : service.totalFee + parseFloat(service.discountValue || 0)
      }
      return this.notApplicable
    },
    renderedEmptyValue (bid, iIndex, index) {
      if (iIndex === 0 && index === 0 && bid.status !== vendorBidStatus.SUBMITTED) {
        return bidStatusMap[bid.status]
      } else {
        return this.emptyValue
      }
    },
    hasBidServiceData (bid) {
      const totalFee = deep(bid, 'content.service.totalFee')
      return totalFee !== undefined && totalFee !== null
    },
    isBidValid (bid) {
      return bid.status === vendorBidStatus.SUBMITTED && this.hasBidServiceData(bid)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings';
@import '~styles/tender/attachments';

table {
  margin: 0;
}

table, thead, tbody, tfoot {
  border: none;
}

table thead th {
  color: #0C3A58;
  padding: 0.5rem 0.625rem 0.625rem;
}

#tenders-comparison {
  padding: 20px;
  padding-bottom: 0;
  max-height: calc(100vh - 126px);
  display: flex;
  flex-direction: column;
  #tenders-comparison-data {
    overflow-x: auto;
    overflow-y: auto;
    thead, tbody {
      display: block;
      td:first-child {
        width: 320px;
        max-width: 320px;
        min-width: 320px;
      }
    }
    tbody{
      tr {
        border: 2px solid #e0e0e0;
      }
      tr:first-child {
        border-top: none;
      }
    }
    thead {
      th {
        border: 2px solid #e0e0e0;
      }
    }
    tbody {
      max-height: calc(100vh - 325px);
    }

    .section-unit-rate, .section-amount {
      min-width: 200px;
      width: 200px;
    }
  }
  .actions {
    margin-top: 10px;
    button {
      float: right;
    }
  }
}

.border-left-right {
  border-left: 2px solid $tender-border-grey;
  border-right: 2px solid $tender-border-grey;
}

.border-top-right {
  border-top: 2px solid $tender-border-grey;
  border-right: 2px solid $tender-border-grey;
}

.border-top {
  border-top: 2px solid $tender-border-grey;
}

.border-right {
  border-right: 2px solid $tender-border-grey;
}

.bold {
  font-weight: bold;
}

.excel-subtitle.right,
.excel-number.right {
  text-align: right;
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.lowestLine {
  font-weight: bold;
  color: #7E9943;
}

.highestLine {
  font-weight: bold;
  color: #ec5b47;
}

td, th {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
}
</style>
